import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button, Input, Select, Card, Modal, PageHeader } from "@/components/Kit";
import { Plus, Trash } from "@phosphor-icons/react";

const COLORS = ["#00E5FF", "#8B5CF6", "#F43F5E", "#10B981", "#F59E0B", "#A1A1AA"];

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "EXPENSE", color: COLORS[0] });
  const [saving, setSaving] = useState(false);

  const load = async () => setCats((await api.get("/categories")).data);
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/categories", { ...form, icon: "Tag" });
      toast.success("Category created");
      setOpen(false);
      setForm({ name: "", type: "EXPENSE", color: COLORS[0] });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/categories/${id}`); toast.success("Category deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const group = (type) => cats.filter((c) => c.type === type);

  const section = (title, type, testid) => (
    <Card className="p-6">
      <h3 className="font-heading font-bold text-lg mb-4">{title}</h3>
      <div className="space-y-2" data-testid={testid}>
        {group(type).length === 0 ? (
          <p className="text-sm text-[#52525B] py-4">No categories.</p>
        ) : group(type).map((c) => (
          <div key={c.id} className="flex items-center justify-between px-3 py-2.5 rounded-md hover:bg-white/[0.03] transition-colors group" data-testid="category-row">
            <div className="flex items-center gap-3">
              <span className="h-3.5 w-3.5 rounded-full" style={{ backgroundColor: c.color }} />
              <span className="text-sm font-medium">{c.name}</span>
            </div>
            <button onClick={() => remove(c.id)} data-testid="delete-category-btn" className="text-[#52525B] hover:text-expense opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash size={16} />
            </button>
          </div>
        ))}
      </div>
    </Card>
  );

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <PageHeader title="Categories" subtitle="Organize your income and spending.">
        <Button onClick={() => setOpen(true)} data-testid="add-category-btn"><Plus size={16} weight="bold" /> Add Category</Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {section("Income", "INCOME", "income-categories")}
        {section("Expense", "EXPENSE", "expense-categories")}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Category">
        <form onSubmit={save} className="space-y-4">
          <Input label="Name" value={form.name} onChange={set("name")} placeholder="e.g. Groceries" required data-testid="category-name" />
          <Select label="Type" value={form.type} onChange={set("type")} data-testid="category-type">
            <option value="EXPENSE">Expense</option>
            <option value="INCOME">Income</option>
          </Select>
          <div>
            <span className="block text-xs font-medium text-[#A1A1AA] mb-2">Color</span>
            <div className="flex gap-2">
              {COLORS.map((col) => (
                <button type="button" key={col} onClick={() => setForm((f) => ({ ...f, color: col }))}
                  data-testid={`color-${col}`}
                  className={`h-8 w-8 rounded-md transition-transform ${form.color === col ? "ring-2 ring-white ring-offset-2 ring-offset-surface scale-110" : ""}`}
                  style={{ backgroundColor: col }} />
              ))}
            </div>
          </div>
          <Button type="submit" disabled={saving} className="w-full" data-testid="save-category-btn">
            {saving ? "Saving..." : "Create Category"}
          </Button>
        </form>
      </Modal>
    </div>
  );
}
